import { getDue } from "../Data/Functions";

// Client portal redesign (v3): the status pills clients see. Solid colors of
// Data/statuses.js with dark text (#030F23); labels in sentence case. Only
// what clients read changes: the stored statuses, office and the emails keep
// their names. Clients read:
//   new          -> "Received"   (office: New)
//   assigned     -> "In progress"(office: Assigned)
//   user-review  -> "Delivered"  (office: Awaits Approval)
//   support-review -> "Quality check", the rest as before.

const pill = (label, color) => ({ label, color });

const ORDER_PILLS = {
  created: pill("Draft", "#C0C0C0"),
  draft: pill("Draft", "#C0C0C0"),
  new: pill("Received", "#FFD280"),
  assigned: pill("In progress", "#8080FF"),
  Assigned: pill("In progress", "#8080FF"),
  Assigend: pill("In progress", "#8080FF"),
  "in-progress": pill("In progress", "#8080FF"),
  "support-review": pill("Quality check", "#F299B3"),
  revision: pill("Revision", "#808080"),
  "user-review": pill("Delivered", "#99CCF2"),
  completed: pill("Completed", "#80C080"),
  cancelled: pill("Cancelled", "#FF8080"),
  refunded: pill("Refunded", "#C080C0"),
  "on-hold": pill("On hold", "#FFFF80"),
  "cancel-hold": pill("Cancellation hold", "#FF8080"),
};

// What each status means for the client, one sentence each: the "i" next to
// the status on the order page.
const ORDER_HELP = {
  created: "You haven't placed this order yet; we saved it so you can finish it any time.",
  new: "We received your order and will assign it to a translator shortly.",
  "in-progress": "A translator is working on your documents, and we'll email you as soon as they're ready.",
  "support-review": "Our team is doing a final review of the translation before we send it to you.",
  revision: "We're making the changes you asked for and will send you the updated translation.",
  "user-review": "Your translation is ready to download: approve it or request a revision (notarization and hard copies go ahead once you approve).",
  completed: "Your order is complete, and its translation stays here for you to download.",
  cancelled: "This order was cancelled; if you were charged, a refund has been issued where applicable.",
  refunded: "This order was refunded, and the payment goes back to where it came from within 3-7 days.",
  "on-hold": "This order is on hold for now, and we'll update you soon.",
  "cancel-hold": "Our team is reviewing your cancellation, so the order is on hold until then.",
};

const orderKey = (order) => {
  if (!order) return "";
  if (order.status === "on-hold" && order.cancellationHold) return "cancel-hold";
  if (["assigned", "Assigned", "Assigend"].includes(order.status)) return "in-progress";
  return order.status;
};

export const orderPill = (order) => ORDER_PILLS[orderKey(order)] || pill(order && order.status ? String(order.status) : "—", "#C0C0C0");
export const orderHelp = (order) => ORDER_HELP[orderKey(order)] || "";

const QUOTE_PILLS = {
  draft: pill("Draft", "#C0C0C0"),
  "in-review": pill("In review", "#FFD280"),
  submitted: pill("In review", "#FFD280"),
  replied: pill("Ready", "#99CCF2"),
  ordered: pill("Ordered", "#80C080"),
  // An interpretation quote paid with its link (it doesn't become an order).
  paid: pill("Paid", "#80C080"),
  cancelled: pill("Cancelled", "#FF8080"),
};
export const quotePill = (quote) => QUOTE_PILLS[quote && quote.status] || pill(quote && quote.status ? String(quote.status) : "—", "#C0C0C0");

// The "i" next to a quote's status (the quote page), one sentence each.
const QUOTE_HELP = {
  draft: "You haven't sent this quote request yet; we saved it so you can finish it any time.",
  "in-review": "We're reviewing your files to price this quote, and we'll email you when it's ready.",
  submitted: "We're reviewing your files to price this quote, and we'll email you when it's ready.",
  replied: "Your quote is priced: pick your options and place the order whenever you're ready.",
  ordered: "This quote is now an order, which you can follow from Orders.",
  cancelled: "This quote was cancelled and can't be ordered anymore.",
};
export const quoteHelp = (quote) => QUOTE_HELP[quote && quote.status] || "";

const INVOICE_PILLS = {
  "pay-later": pill("Unpaid", "#FFD280"),
  overdue: pill("Overdue", "#FF8080"),
  paid: pill("Paid", "#80C080"),
  "paid-manually": pill("Paid", "#80C080"),
  "on-hold": pill("On hold", "#FFFF80"),
  cancelled: pill("Cancelled", "#FF8080"),
  refunded: pill("Refunded", "#C080C0"),
};
export const invoicePill = (invoice) => INVOICE_PILLS[invoice && invoice.status] || pill(invoice && invoice.status ? String(invoice.status) : "—", "#C0C0C0");

// Paid / Unpaid / On invoice next to an order's total in the client's Orders
// list, by the office lists' rule (officeList/officeFormat paymentState; not
// imported, it would bring office code into the client's lists): paid when
// the order has a payment reference ("paid" once its invoice is paid), on
// invoice when it goes on the team's monthly invoice ("auto-billing"),
// unpaid otherwise ("pay-later"). No chip on drafts, cancelled or refunded
// orders, nor while a payment is being checked ("on-hold").
export const orderPayState = (order) => {
  if (!order || ["created", "draft", "cancelled", "refunded"].includes(order.status)) return null;
  const ref = order.paypalOrderID;
  if (ref === "on-hold") return null;
  if (ref === "auto-billing") return "invoice";
  if (typeof ref === "string" && ref !== "" && ref !== "pay-later") return "paid";
  return "unpaid";
};
export const PAY_LABEL = { paid: "Paid", unpaid: "Unpaid", invoice: "On invoice" };

export const INVOICE_OPEN = ["pay-later", "overdue"];
export const isInvoiceOpen = (invoice) => Boolean(invoice && INVOICE_OPEN.includes(invoice.status));

// One the client can pay now: an unpaid single invoice, or any overdue one
// (a team's monthly invoice is charged automatically until it is overdue).
export const payableInvoice = (invoice) => Boolean(invoice && (
  (invoice.status === "pay-later" && invoice.type !== "multipleInvoice") || invoice.status === "overdue"));

// ---------- When an order should be ready ----------

// Orders being worked on: placed, not delivered yet (on hold has no estimate).
export const IN_PROGRESS = ["new", "assigned", "Assigned", "Assigend", "in-progress", "support-review", "revision"];
export const isInProgress = (order) => Boolean(order && IN_PROGRESS.includes(order.status) && !order.cancellationHold);

const HOUR = 60 * 60 * 1000;

// When it should be ready (ms), rounded up to the hour, or null. The office's
// due date once a translator has it (orders.due; revisions: revisionDue);
// before that, the time the checkout promised: the order's date plus the
// turnaround of its pages (getDue, the checkout's rule).
export const estimatedDelivery = (order) => {
  if (!isInProgress(order)) return null;
  let at = null;
  if (order.status === "revision" && Number(order.revisionDue) > 0) {
    at = Number(order.revisionDue);
  } else if (Number(order.due) > 0 && order.status !== "new") {
    at = Number(order.due);
  } else {
    const placed = Number(order.timestamp) || Date.now();
    const span = getDue(true, order) - Date.now();
    at = placed + Math.max(span, 0);
  }
  return Math.ceil(at / HOUR) * HOUR;
};

const fmt = (ms, opts) => {
  try {
    return new Date(ms).toLocaleString("en-US", opts);
  } catch (e) {
    const { timeZoneName, ...rest } = opts;
    return new Date(ms).toLocaleString("en-US", rest);
  }
};

const dayStart = (ms) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// "Today, 6:00 PM EDT" / "Tomorrow, 10:00 AM EDT" / "Wed, Oct 7, 10:00 AM EDT",
// in the viewer's time zone (as the checkout's "Estimated delivery").
export const formatWhen = (ms, { lower = false } = {}) => {
  const time = fmt(ms, { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  const days = Math.round((dayStart(ms) - dayStart(Date.now())) / (24 * HOUR));
  if (days === 0) return `${lower ? "today" : "Today"}, ${time}`;
  if (days === 1) return `${lower ? "tomorrow" : "Tomorrow"}, ${time}`;
  return `${fmt(ms, { weekday: "short", month: "short", day: "numeric" })}, ${time}`;
};

// The estimate as the order page shows it ("Any time now" once it passed).
export const deliveryText = (order) => {
  const at = estimatedDelivery(order);
  if (!at) return null;
  return at <= Date.now() ? "Any time now" : formatWhen(at);
};

// The list's tooltip on the status ("Est. today, 6:00 PM EDT").
export const deliveryTip = (order) => {
  const at = estimatedDelivery(order);
  if (!at) return null;
  return at <= Date.now() ? "Est. any time now" : `Est. ${formatWhen(at, { lower: true })}`;
};

// ---------- When an invoice is due ----------

const DAY = 24 * HOUR;
const utcDay = (ms, plus = 0) => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + plus);
};

// A single (case-by-case) invoice becomes overdue on its day 30 (net 30; functions
// Invoices/reminders.js), so it is due through day 29. A monthly one is
// charged on its day 3 (teamInvoices-chargePendingInvoices).
export const invoiceDueAt = (invoice) => {
  if (!invoice || !Number(invoice.timestamp)) return null;
  return invoice.type === "multipleInvoice" ? utcDay(invoice.timestamp, 3) : utcDay(invoice.timestamp, 29);
};

const shortDay = (ms) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

// Under the pill, only for Unpaid / Overdue: "Due Oct 24" / "12 days late".
export const invoiceDueText = (invoice) => {
  if (!isInvoiceOpen(invoice)) return "";
  const due = invoiceDueAt(invoice);
  if (!due) return "";
  const late = Math.floor((utcDay(Date.now()) - due) / DAY);
  // A monthly invoice is charged (and retried) on its own until office marks it overdue.
  if (invoice.type === "multipleInvoice" && invoice.status === "pay-later") return `Due ${shortDay(due)}`;
  if (invoice.status === "overdue" || late > 0) {
    return late <= 0 ? "Overdue" : `${late} day${late === 1 ? "" : "s"} late`;
  }
  return `Due ${shortDay(due)}`;
};

export const invoiceDaysLate = (invoice) => {
  const due = invoiceDueAt(invoice);
  if (!due) return 0;
  return Math.max(0, Math.floor((utcDay(Date.now()) - due) / DAY));
};
